import { BundleTreeTypes } from '@repo/atomic-components';
import { formatDateString, isLlmErrorMarker } from '@repo/utils';
import { BundleSectionsTreeNode } from '@/api';

// One dash per level of nesting, so a pasted index keeps the shape of the
// tree: provider at the root, then category, sub-category, cluster.
const DEPTH_MARKER = '-';

// Clusters render their short title as the headline with the document name
// and date underneath, so flatten those three parts onto the single line.
const formatNodeLine = (node: BundleSectionsTreeNode, depth: number): string => {
  const prefix = DEPTH_MARKER.repeat(depth);
  if (node.type === BundleTreeTypes.CLUSTER) {
    const shortTitle = node.shortTitle && isLlmErrorMarker(node.shortTitle) ? undefined : node.shortTitle;
    const title = shortTitle || node.name;
    const name = node.name && node.name !== title ? ` - ${node.name}` : '';
    const date = node.date ? ` (${formatDateString(node.date)})` : '';
    return `${prefix}${title}${name}${date}`;
  }
  return `${prefix}${node.name}`;
};

/**
 * Flattens the index tree into indented plain text for the clipboard, e.g.
 *
 *   PROVIDER
 *   -CATEGORY
 *   --SUB CATEGORY
 *   ---CLUSTER
 *
 * Takes whatever tree is currently in the store, so a filtered index copies
 * only the rows the user can see.
 */
export const buildIndexClipboardText = (sections: BundleSectionsTreeNode[]): string => {
  const lines: string[] = [];

  const walk = (nodes: BundleSectionsTreeNode[], depth: number) => {
    for (const node of nodes) {
      lines.push(formatNodeLine(node, depth));
      if (node.children?.length) {
        walk(node.children, depth + 1);
      }
    }
  };

  walk(sections, 0);

  return lines.join('\n');
};
